import { ActionIcon, Alert, Button, Group, Modal, Table, Tooltip } from '@mantine/core'
import { IconCheck, IconExternalLink, IconRefresh, IconUserCheck } from '@tabler/icons-react'
import { MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { cn } from '@/utils/cn'
import type { ZocdocAppointmentRow } from './AppointmentDetailsModal'
import { useZocdocPatientLink } from './useZocdocPatientLink'
import { HINT, ID_CHIP, PRIMARY_BTN, SECONDARY_BTN, TABLE_HEAD, TABLE_ROW } from './tokens'
import { formatDob } from './zocdocFormat'

/**
 * The charts that may be this booking's patient, and the two decisions an operator can make about
 * them — reached from the row's own chart chip rather than by opening the whole appointment.
 *
 * **A shortcut, not a second implementation.** The judgement and the writes are the details
 * popup's, via `useZocdocPatientLink`; what this adds is reaching them in one click from the place
 * the operator noticed the problem. The candidate list is the commonest thing on this screen that
 * needs acting on — a booking whose patient we already hold, unlinked — and it used to be a label
 * that named that and offered nothing.
 *
 * **Candidates, never a match.** They are found on email plus date of birth, which is a guess about
 * a person; the wording says so, and nothing here links anything until somebody presses a button.
 *
 * **Link or close — never create.** Creating a chart from here invited a second copy of a patient
 * we already hold. An operator who judges that no candidate is this person closes the popup.
 */
export function PatientCandidatesModal({
  appointment,
  onClose,
  onLinked,
}: {
  /** The row whose candidates are being judged; `null` closes the modal. */
  appointment: ZocdocAppointmentRow | null
  onClose: () => void
  /** A chart was linked, so the list is stale — the caller re-reads it. */
  onLinked: () => void
}) {
  const patient = appointment?.participants?.patient
  const zocdocPatientId = patient?.patientId
  const { linking, linkError, confirmSamePatient } = useZocdocPatientLink(zocdocPatientId, onLinked)

  const candidates = appointment?.associatedPatients ?? []
  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ')

  return (
    <Modal
      opened={appointment !== null}
      onClose={onClose}
      size="46rem"
      radius={18}
      classNames={{
        content: MODAL_CONTENT,
        title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
      }}
      title="beHuman charts that may be this patient"
    >
      {appointment && (
        <div className="flex flex-col gap-3">
          <p className={cn([HINT, 'm-0'])}>
            Matched on email and date of birth against{' '}
            <span className="font-bold text-[#1F2733]">{patientName || 'this booking'}</span>
            {zocdocPatientId ? (
              <>
                {' '}
                (<span className={ID_CHIP}>{zocdocPatientId}</span>)
              </>
            ) : null}
            . Candidates for you to judge — nothing here links a chart until you say so.
          </p>

          {linkError && (
            <Alert color="red" radius="md" title="Chart not linked">
              <span className="text-[13px]">{linkError}</span>
            </Alert>
          )}

          {/* Without an id there is nothing to write, and every button below would refuse. Said
              once, at the top, rather than as four identical disabled tooltips. */}
          {!zocdocPatientId && (
            <Alert color="yellow" radius="md" title="No Zocdoc patient id">
              <span className="text-[13px]">
                Zocdoc gave no patient id for this booking, so no chart can be linked to it.
              </span>
            </Alert>
          )}

          {/* One block per candidate: the booking's Zocdoc patient and the beHuman chart as two
              rows under the same columns, so the operator compares field against field rather
              than reading one record from the intro text and the other from a table. */}
          {candidates.map((candidate) => (
            <div
              key={candidate.patientId}
              className="flex flex-col gap-2 rounded-xl border border-[#ECEEF1] p-3"
            >
              <Table verticalSpacing={6} fz="xs">
                <Table.Thead>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[96px]'])}>Source</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Name</Table.Th>
                    <Table.Th className={TABLE_HEAD}>DOB</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Email</Table.Th>
                    <Table.Th className={TABLE_HEAD}>Phone</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Td className="font-bold text-[#6B7280]">Zocdoc</Table.Td>
                    <Table.Td className="font-bold text-[#1F2733]">{patientName || '—'}</Table.Td>
                    <Table.Td>{formatDob(patient?.dateOfBirth) ?? '—'}</Table.Td>
                    <Table.Td className="break-all">{patient?.email ?? '—'}</Table.Td>
                    <Table.Td>{patient?.phone ?? '—'}</Table.Td>
                  </Table.Tr>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Td className="font-bold text-[#6B7280]">
                      <Group gap={2} wrap="nowrap">
                        beHuman
                        {/* A real anchor, not an onClick: middle-click, cmd-click and "copy link"
                            all work, and a new tab keeps the operator's place in the list. */}
                        <Tooltip label={`Open chart ${candidate.patientId} in a new tab`} withArrow>
                          <ActionIcon
                            component="a"
                            href={`/patients/${candidate.patientId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="subtle"
                            color="gray"
                            size="sm"
                            aria-label={`Open the chart for ${candidate.firstName ?? candidate.patientId} in a new tab`}
                          >
                            <IconExternalLink size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td className="font-bold text-[#1F2733]">
                      {candidate.firstName ?? '—'}
                    </Table.Td>
                    <Table.Td>{formatDob(candidate.dob) ?? '—'}</Table.Td>
                    <Table.Td className="break-all">{candidate.email ?? '—'}</Table.Td>
                    <Table.Td>{candidate.phoneNumber ?? '—'}</Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>

              {/* Three states, and the third is the one worth getting right: this chart carries
                  THIS Zocdoc id (done), it carries none (offer the link), or it carries a DIFFERENT
                  one — not a dead end and not an append, because a chart holds one Zocdoc patient,
                  so it offers to *replace*. */}
              {zocdocPatientId && candidate.zocdocPatientIds.includes(zocdocPatientId) ? (
                <span className="behuman-v2-mulish inline-flex items-center justify-center gap-1 rounded-md bg-[#DCFCE7] px-2 py-1.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase">
                  <IconCheck size={12} />
                  synced with beHuman
                </span>
              ) : candidate.zocdocPatientIds.length > 0 ? (
                <>
                  {/* The id is in the title, not printed: what the operator needs at a glance is
                      that this chart is spoken for. */}
                  <span
                    className={cn([HINT, 'text-[#B45309]'])}
                    title={`This chart carries Zocdoc patient ${candidate.zocdocPatientIds.join(', ')}`}
                  >
                    This chart is already linked to another Zocdoc patient.
                  </span>
                  <Button
                    {...SECONDARY_BTN}
                    fullWidth
                    color="#B45309"
                    leftSection={<IconRefresh size={14} />}
                    loading={linking === candidate.patientId}
                    disabled={!zocdocPatientId || linking !== null}
                    title={
                      zocdocPatientId
                        ? `Drop ${candidate.zocdocPatientIds.join(', ')} from this chart and record ${zocdocPatientId} instead`
                        : 'Zocdoc gave no patient id for this appointment, so there is nothing to link'
                    }
                    onClick={() => void confirmSamePatient(candidate.patientId, true)}
                  >
                    Replace the link
                  </Button>
                </>
              ) : (
                <Button
                  {...PRIMARY_BTN}
                  fullWidth
                  leftSection={<IconUserCheck size={16} />}
                  loading={linking === candidate.patientId}
                  disabled={!zocdocPatientId || linking !== null}
                  title={
                    zocdocPatientId
                      ? `Write Zocdoc patient ${zocdocPatientId} onto this chart`
                      : 'Zocdoc gave no patient id for this appointment, so there is nothing to link'
                  }
                  onClick={() => void confirmSamePatient(candidate.patientId)}
                >
                  Confirm it is the same person so we can link the beHuman ⇄ Zocdoc patient profile
                </Button>
              )}
            </div>
          ))}

          {/* No "Import patient" here. A matching chart is on screen, and creating another beside
              it is exactly how duplicate patients were being invited. */}
          <Group justify="flex-end">
            <Button {...SECONDARY_BTN} onClick={onClose} disabled={linking !== null}>
              Close
            </Button>
          </Group>
        </div>
      )}
    </Modal>
  )
}
